import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/providers/apiProvider';
import { User } from '@/types/user';

export const useGetUsers = () => {
  const httpClient = useApi();

  const getUsers = async () => {
    const response = await httpClient.get<User[]>('/users/');
    return response.data;
  };

  return useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });
};

export const useGetUser = (userId: string | undefined) => {
  const httpClient = useApi();

  const getUser = async (userId: string) => {
    const response = await httpClient.get<User>(`/users/${userId.trim()}/`);
    return response.data;
  };

  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId ?? ''),
    enabled: !!userId,
  });
};

export type EnvironmentAccess = {
  /** This API's environment: 'dev', 'prod' or 'local'. */
  environment: string;
  /** The environment the stored snapshot came from; null before the first daily check. */
  peer: string | null;
  checked_at: string | null;
  /** Keyed by lowercase email; internal users in either environment. */
  users: Record<
    string,
    { here: boolean; there: boolean; differences: string[] }
  >;
};

/** Admins only (api#312). Disabled for everyone else; a 404 means the API predates it. */
export const useGetEnvironmentAccess = (enabled: boolean) => {
  const httpClient = useApi();

  const getEnvironmentAccess = async () => {
    const response = await httpClient.get<EnvironmentAccess>(
      '/users/environment-access/'
    );
    return response.data;
  };

  return useQuery({
    queryKey: ['users', 'environment-access'],
    queryFn: getEnvironmentAccess,
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};
